import { format } from 'date-fns';
import { useMyOutstandingAwpMembers } from '@/hooks/members/useMyOutstandingAwpMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import { sortFnTags } from '@/lib/member-views/sortRows';
import type { OutstandingAwpMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<OutstandingAwpMemberRow>;

/**
 * Outstanding AWP — the practice's Active and Suspended, non-Scholarship members whose last AWP is
 * over a year old or was never recorded, for following up.
 *
 * The report is the 24 columns below, in `csv.order`: legacy's report less its Title column, which
 * this view's API doesn't return. Established and Last AWP are the table's own fields; the table's
 * Patient and Phone combine two values each, so the report has its own name and phone columns.
 */
export const outstandingAwpMembersDataset: MemberDataset<OutstandingAwpMemberRow> = {
  key: 'outstanding-awp',
  rowKey: (row) => row.id,
  useRows: useMyOutstandingAwpMembers,
  alphaKey: (row) => row.lastName,
  // No `defaultSort`: the server already returns the most overdue first, and a client sort would mark
  // the Last AWP header as active, which the view opens without.
  // The same provisional ceiling as All Active; these members are a subset of it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_Outstanding_AWP_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round, with the MDVIP ID.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'lastAwp',
      label: 'memberViews.columns.lastAwp',
      width: 'sm',
      value: (row) => row.lastAwpDate,
      format: 'date',
      table: true,
      sortable: true,
      // A member never seen for an AWP is the most overdue, so a missing date sorts as the oldest.
      sortUndefined: -1,
      csv: { order: 13, label: 'memberViews.csv.columns.lastAwpDate' },
    },
    {
      id: 'established',
      label: 'memberViews.columns.established',
      width: 'sm',
      value: (row) => row.memberSince,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 4, label: 'memberViews.csv.columns.dateEstablished' },
    },
    {
      id: 'phone',
      label: 'memberViews.columns.phone',
      width: 'lg',
      value: (row) => row.mobilePhone ?? row.phone,
      format: 'phone',
      table: true,
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      // No tags is a missing value, so untagged rows sort last both ways, as the design asks.
      value: (row) => (row.priorityTags.length > 0 ? row.priorityTags : null),
      table: true,
      sortable: true,
      sortFn: sortFnTags,
    },
    reportOnly('lastName', 1, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('firstName', 2, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('mdvipId', 3, 'memberViews.csv.columns.mdvipId', (row) => row.mdvipId),
    reportOnly('renewalDate', 5, 'memberViews.csv.columns.renewalDate', (row) => row.renewalDate, 'date'),
    reportOnly('mailingStreet', 6, 'memberViews.csv.columns.mailingStreet', (row) => row.mailingStreet),
    reportOnly('city', 7, 'memberViews.csv.columns.city', (row) => row.mailingCity),
    reportOnly('state', 8, 'memberViews.csv.columns.state', (row) => row.mailingState),
    reportOnly('zip', 9, 'memberViews.csv.columns.zip', (row) => row.mailingZip),
    // The phone numbers as the API returns them, as legacy's report printed them.
    reportOnly('homePhone', 10, 'memberViews.csv.columns.phone', (row) => row.phone),
    reportOnly('cellPhone', 11, 'memberViews.csv.columns.cellPhone', (row) => row.mobilePhone),
    reportOnly('email', 12, 'memberViews.csv.columns.email', (row) => row.email),
    reportOnly('status', 14, 'memberViews.csv.columns.status', (row) => row.memberStatus),
    reportOnly('dob', 15, 'memberViews.csv.columns.dob', (row) => row.dateOfBirth, 'date'),
    reportOnly(
      'paymentSchedule',
      16,
      'memberViews.csv.columns.paymentSchedule',
      (row) => row.currentAgreementPaymentSchedule,
    ),
    // The current agreement's fee, not the membership's list price.
    reportOnly(
      'membershipFee',
      17,
      'memberViews.csv.columns.membershipFee',
      (row) => row.currentAgreementMsrp,
      'currency',
    ),
    reportOnly('autoPay', 18, 'memberViews.csv.columns.autoPay', (row) => row.autoPay, 'yesBlank'),
    reportOnly('lastPaymentDate', 19, 'memberViews.csv.columns.lastPaymentDate', (row) => row.lastPaymentDate, 'date'),
    reportOnly(
      'lastPaymentAmount',
      20,
      'memberViews.csv.columns.lastPaymentAmount',
      (row) => row.lastPaymentAmount,
      'currency',
    ),
    reportOnly('amountDue', 21, 'memberViews.csv.columns.amountDue', (row) => row.currentBalanceFormula, 'currency'),
    reportOnly('daysPastDue', 22, 'memberViews.csv.columns.daysPastDue', (row) => row.billingDaysPastDue, 'number'),
    // "No" survives here, unlike Auto Pay: legacy printed this field's own Yes/No.
    reportOnly(
      'billToCorporateAccount',
      23,
      'memberViews.csv.columns.billToCorporateAccount',
      (row) => row.billToNationalAccount,
      'yesNoBlank',
    ),
    reportOnly('corporateAccount', 24, 'memberViews.csv.columns.corporateAccount', (row) => row.nationalAccountName),
  ],
};
